import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

// HUD color tokens (matches PatientTracForge)
const C = {
  navy:     '#060e1c',
  navyDeep: '#0a1628',
  gold:     '#c9a96e',
  goldSoft: 'rgba(201,169,110,0.6)',
  goldDim:  'rgba(201,169,110,0.2)',
  goldLine: 'rgba(201,169,110,0.1)',
  text:     '#e2e8f0',
  textDim:  'rgba(226,232,240,0.6)',
  green:    '#86efac',
  greenBg:  'rgba(34,197,94,0.1)',
  red:      '#fca5a5',
  redBg:    'rgba(239,68,68,0.1)',
  yellow:   '#fcd34d',
  yellowBg: 'rgba(245,158,11,0.1)',
};

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenError, setTokenError] = useState(null);

  // Password strength indicators
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: [],
  });

  useEffect(() => {
    // Check if we have a valid session from the reset link
    const checkSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) throw error;
        
        if (!session) {
          setTokenError('Invalid or expired reset link. Please request a new password reset.');
        }
      } catch {
        setTokenError('Invalid or expired reset link. Please request a new password reset.');
      } finally {
        setValidatingToken(false);
      }
    };

    checkSession();
  }, []);

  useEffect(() => {
    if (newPassword) {
      const strength = calculatePasswordStrength(newPassword);
      setPasswordStrength(strength);
    } else {
      setPasswordStrength({ score: 0, feedback: [] });
    }
  }, [newPassword]);

  const calculatePasswordStrength = (password) => {
    let score = 0;
    const feedback = [];

    // Length check
    if (password.length >= 8) {
      score += 1;
    } else {
      feedback.push('At least 8 characters');
    }

    // Uppercase check
    if (/[A-Z]/.test(password)) {
      score += 1;
    } else {
      feedback.push('One uppercase letter');
    }

    // Lowercase check
    if (/[a-z]/.test(password)) {
      score += 1;
    } else {
      feedback.push('One lowercase letter');
    }

    // Number check
    if (/[0-9]/.test(password)) {
      score += 1;
    } else {
      feedback.push('One number');
    }

    // Special character check
    if (/[^A-Za-z0-9]/.test(password)) {
      score += 1;
    } else {
      feedback.push('One special character');
    }

    return { score, feedback };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validation
      if (newPassword.length < 8) {
        throw new Error('Password must be at least 8 characters long');
      }

      if (newPassword !== confirmPassword) {
        throw new Error('Passwords do not match');
      }

      if (passwordStrength.score < 3) {
        throw new Error('Password is too weak. Please use a stronger password.');
      }

      // Update password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) throw updateError;

      setSuccess(true);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);

    } catch (err) {
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStrengthColor = () => {
    if (passwordStrength.score < 2) return C.red;
    if (passwordStrength.score < 4) return C.yellow;
    return C.green;
  };

  const getStrengthLabel = () => {
    if (passwordStrength.score < 2) return 'Weak';
    if (passwordStrength.score < 4) return 'Fair';
    return 'Strong';
  };

  const s = styles;

  if (validatingToken) {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <div style={s.loadingContainer}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={C.gold} strokeWidth="2" style={s.spinner}>
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
            </svg>
            <div style={s.loadingText}>Validating reset link...</div>
          </div>
        </div>
      </div>
    );
  }

  if (tokenError) {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <div style={s.errorContainer}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={C.red} strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <h2 style={s.errorTitle}>Reset Link Invalid</h2>
            <p style={s.errorText}>{tokenError}</p>
            <button 
              onClick={() => navigate('/forgot-password')}
              style={s.button}
            >
              Request New Reset Link
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.card}>
        {/* Header */}
        <div style={s.header}>
          <div style={s.logoLine}>
            <svg width="22" height="22" viewBox="0 0 32 32" fill="none">
              <polygon points="16,2 30,16 16,30 2,16" fill="none" stroke={C.gold} strokeWidth="1.5"/>
              <polygon points="16,7 25,16 16,25 7,16" fill={C.gold} opacity="0.3"/>
              <polygon points="16,11 21,16 16,21 11,16" fill={C.gold}/>
            </svg>
            <span style={s.logoText}>PatientTrac Mind</span>
          </div>
          <div style={s.tagline}>BEHAVIORAL HEALTH EMR</div>
        </div>

        {/* Success State */}
        {success ? (
          <div style={s.successContainer}>
            <div style={s.successIcon}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={C.green} strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
            <h2 style={s.successTitle}>Password Reset Successful!</h2>
            <p style={s.successText}>
              Your password has been updated. Redirecting you to login...
            </p>
          </div>
        ) : (
          <>
            {/* Title */}
            <div style={s.title}>Set New Password</div>
            <div style={s.subtitle}>
              Choose a strong password to protect your account.
            </div>

            {/* Error Message */}
            {error && (
              <div style={s.error}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.red} strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                {error}
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} style={s.form}>
              {/* New Password */}
              <div style={s.inputGroup}>
                <label style={s.label}>New Password</label>
                <div style={s.passwordWrapper}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    style={s.input}
                    disabled={loading}
                    autoFocus
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={s.eyeButton}
                    disabled={loading}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.textDim} strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.textDim} strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>

                {/* Password Strength Indicator */}
                {newPassword && (
                  <div style={s.strengthContainer}>
                    <div style={s.strengthBar}>
                      <div style={{
                        ...s.strengthFill,
                        width: `${(passwordStrength.score / 5) * 100}%`,
                        backgroundColor: getStrengthColor(),
                      }}/>
                    </div>
                    <div style={{
                      ...s.strengthLabel,
                      color: getStrengthColor(),
                    }}>
                      {getStrengthLabel()}
                    </div>
                    {passwordStrength.feedback.length > 0 && (
                      <div style={s.strengthFeedback}>
                        <div style={s.strengthFeedbackTitle}>Password needs:</div>
                        <ul style={s.strengthList}>
                          {passwordStrength.feedback.map((item, index) => (
                            <li key={index} style={s.strengthItem}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              <div style={s.inputGroup}>
                <label style={s.label}>Confirm New Password</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter new password"
                  style={s.input}
                  disabled={loading}
                  required
                />
                {confirmPassword && newPassword !== confirmPassword && (
                  <div style={s.matchError}>Passwords do not match</div>
                )}
              </div>

              <button 
                type="submit" 
                style={{
                  ...s.button,
                  ...(loading || passwordStrength.score < 3 ? s.buttonDisabled : {}),
                }}
                disabled={loading || passwordStrength.score < 3 || newPassword !== confirmPassword}
              >
                {loading ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={s.spinner}>
                      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                    </svg>
                    Resetting Password...
                  </>
                ) : (
                  'Reset Password'
                )}
              </button>
            </form>
          </>
        )}

        {/* HIPAA Notice */}
        <div style={s.hipaa}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={C.goldSoft} strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
          HIPAA Compliant • Secure Authentication
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: `linear-gradient(135deg, ${C.navyDeep} 0%, ${C.navy} 100%)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    fontFamily: '"DM Sans", -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
  },
  card: {
    background: C.navy,
    borderRadius: '16px',
    border: `1px solid ${C.goldLine}`,
    padding: '3rem',
    width: '100%',
    maxWidth: '440px',
    boxShadow: `0 20px 60px rgba(0,0,0,0.5), 0 0 1px ${C.goldLine}`,
  },
  header: {
    marginBottom: '2.5rem',
    textAlign: 'center',
  },
  logoLine: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    marginBottom: '8px',
  },
  logoText: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: C.text,
    letterSpacing: '0.5px',
  },
  tagline: {
    fontSize: '0.75rem',
    color: C.goldSoft,
    letterSpacing: '2px',
    fontWeight: '600',
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: '700',
    color: C.text,
    marginBottom: '0.75rem',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: '0.95rem',
    color: C.textDim,
    lineHeight: '1.5',
    marginBottom: '2rem',
    textAlign: 'center',
  },
  error: {
    background: C.redBg,
    border: `1px solid rgba(239,68,68,0.3)`,
    borderRadius: '8px',
    padding: '0.875rem 1rem',
    marginBottom: '1.5rem',
    color: C.red,
    fontSize: '0.875rem',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  label: {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: C.text,
    letterSpacing: '0.3px',
  },
  passwordWrapper: {
    position: 'relative',
  },
  input: {
    background: C.navyDeep,
    border: `1px solid ${C.goldLine}`,
    borderRadius: '8px',
    padding: '0.875rem 1rem',
    paddingRight: '3rem',
    fontSize: '1rem',
    color: C.text,
    outline: 'none',
    transition: 'all 0.2s',
    fontFamily: 'inherit',
    width: '100%',
  },
  eyeButton: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  strengthContainer: {
    marginTop: '0.5rem',
  },
  strengthBar: {
    height: '4px',
    background: C.goldLine,
    borderRadius: '2px',
    overflow: 'hidden',
    marginBottom: '0.5rem',
  },
  strengthFill: {
    height: '100%',
    transition: 'all 0.3s',
  },
  strengthLabel: {
    fontSize: '0.75rem',
    fontWeight: '600',
    marginBottom: '0.5rem',
  },
  strengthFeedback: {
    background: C.yellowBg,
    border: `1px solid rgba(245,158,11,0.3)`,
    borderRadius: '6px',
    padding: '0.75rem',
    marginTop: '0.5rem',
  },
  strengthFeedbackTitle: {
    fontSize: '0.75rem',
    color: C.yellow,
    fontWeight: '600',
    marginBottom: '0.5rem',
  },
  strengthList: {
    margin: 0,
    paddingLeft: '1.25rem',
    fontSize: '0.75rem',
    color: C.textDim,
  },
  strengthItem: {
    marginBottom: '0.25rem',
  },
  matchError: {
    fontSize: '0.75rem',
    color: C.red,
    marginTop: '0.25rem',
  },
  button: {
    background: `linear-gradient(135deg, ${C.gold} 0%, ${C.goldSoft} 100%)`,
    border: 'none',
    borderRadius: '8px',
    padding: '1rem',
    fontSize: '1rem',
    fontWeight: '600',
    color: C.navy,
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontFamily: 'inherit',
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  spinner: {
    animation: 'spin 1s linear infinite',
  },
  loadingContainer: {
    textAlign: 'center',
    padding: '2rem 0',
  },
  loadingText: {
    marginTop: '1rem',
    color: C.textDim,
    fontSize: '0.95rem',
  },
  errorContainer: {
    textAlign: 'center',
    padding: '1rem 0',
  },
  errorTitle: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: C.text,
    marginTop: '1rem',
    marginBottom: '1rem',
  },
  errorText: {
    fontSize: '0.95rem',
    color: C.textDim,
    lineHeight: '1.6',
    marginBottom: '2rem',
  },
  successContainer: {
    textAlign: 'center',
    padding: '1rem 0',
  },
  successIcon: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '1.5rem',
  },
  successTitle: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: C.text,
    marginBottom: '1rem',
  },
  successText: {
    fontSize: '1rem',
    color: C.textDim,
    lineHeight: '1.6',
  },
  hipaa: {
    marginTop: '2rem',
    paddingTop: '1.5rem',
    borderTop: `1px solid ${C.goldLine}`,
    fontSize: '0.75rem',
    color: C.goldSoft,
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
};

// Add spinner animation
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);
